import { DragEvent, KeyboardEvent, ReactNode, useEffect, useState } from 'react'
import { FaArrowDown, FaArrowUp, FaCheck, FaGripVertical, FaPen, FaPlus, FaTimes, FaTrash } from 'react-icons/fa'

export interface NameListItem {
  id: number
  name: string
  /** A short note under the name, e.g. "12 jobs". */
  meta?: string
}

interface EditableNameListProps {
  items: NameListItem[]
  onAdd: (name: string) => Promise<boolean>
  onRename: (item: NameListItem, name: string) => Promise<boolean>
  onDelete: (item: NameListItem) => Promise<void>
  addPlaceholder: string
  emptyText: string
  maxLength: number
  /** Makes rows selectable, e.g. to show a notifiable work's documents. */
  selectedId?: number | null
  onSelect?: (item: NameListItem) => void
  /**
   * Makes the list orderable by dragging a row, or with the move up / move
   * down buttons for the keyboard and touch screens. Called with every id in
   * the new order.
   */
  onReorder?: (orderedIds: number[]) => Promise<boolean | void>
  /** Extra controls for a row, shown before its rename and remove buttons. */
  renderControls?: (item: NameListItem) => ReactNode
  disabled?: boolean
}

/**
 * A list of names that can be added to, renamed in place and removed. The
 * handlers return false (or throw) when the server refused, so the typed
 * text stays for the user to correct.
 */
const EditableNameList = ({
  items,
  onAdd,
  onRename,
  onDelete,
  addPlaceholder,
  emptyText,
  maxLength,
  selectedId,
  onSelect,
  onReorder,
  renderControls,
  disabled,
}: EditableNameListProps) => {
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [busy, setBusy] = useState(false)

  // The order just asked for, shown while it saves so the list doesn't jump
  // back until the reload arrives. Cleared when new items come in, or if the
  // save fails.
  const [pendingOrder, setPendingOrder] = useState<number[] | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)

  // Compared by content: the parent builds a new array on every render.
  const itemsKey = items.map((i) => `${i.id}:${i.name}`).join('|')
  useEffect(() => setPendingOrder(null), [itemsKey])

  const shown = pendingOrder
    ? pendingOrder.map((id) => items.find((i) => i.id === id)).filter((i): i is NameListItem => !!i)
    : items

  const locked = disabled || busy

  const run = async (work: () => Promise<boolean | void>) => {
    setBusy(true)
    try {
      return await work()
    } finally {
      setBusy(false)
    }
  }

  const add = async () => {
    if (!newName.trim()) return
    if (await run(() => onAdd(newName.trim()))) setNewName('')
  }

  const reorder = async (from: number, to: number) => {
    if (!onReorder || from === to || to < 0 || to >= shown.length) return
    const ids = shown.map((i) => i.id)
    const [moved] = ids.splice(from, 1)
    ids.splice(to, 0, moved)
    setPendingOrder(ids)
    if ((await run(() => onReorder(ids))) === false) setPendingOrder(null)
  }

  const move = (index: number, by: -1 | 1) => reorder(index, index + by)

  // --- Dragging ------------------------------------------------------------

  const canDrag = !!onReorder && !locked && editingId === null && shown.length > 1

  const endDrag = () => {
    setDragIndex(null)
    setOverIndex(null)
  }

  const onDragStart = (e: DragEvent<HTMLLIElement>, index: number) => {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    // Firefox won't start a drag without some data.
    e.dataTransfer.setData('text/plain', String(shown[index].id))
  }

  const onDragOver = (e: DragEvent<HTMLLIElement>, index: number) => {
    if (dragIndex === null) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (overIndex !== index) setOverIndex(index)
  }

  const onDrop = (e: DragEvent<HTMLLIElement>, index: number) => {
    e.preventDefault()
    const from = dragIndex
    endDrag()
    if (from !== null) reorder(from, index)
  }

  // Where the dragged row would land, drawn as a line above or below the row
  // it is over.
  const dropClass = (index: number) =>
    dragIndex === null || overIndex !== index || dragIndex === index
      ? ''
      : dragIndex < index
        ? ' lk-row--drop-after'
        : ' lk-row--drop-before'

  const startEdit = (item: NameListItem) => {
    setEditingId(item.id)
    setEditName(item.name)
  }

  const saveEdit = async (item: NameListItem) => {
    const name = editName.trim()
    if (!name) return
    if (name === item.name) {
      setEditingId(null)
      return
    }
    if (await run(() => onRename(item, name))) setEditingId(null)
  }

  const onEditKey = (e: KeyboardEvent<HTMLInputElement>, item: NameListItem) => {
    if (e.key === 'Enter') saveEdit(item)
    if (e.key === 'Escape') setEditingId(null)
  }

  return (
    <div className='lk-list'>
      {items.length === 0 && <p className='lk-empty'>{emptyText}</p>}

      <ul>
        {shown.map((item, index) =>
          editingId === item.id ? (
            <li key={item.id} className='lk-row lk-row--editing'>
              <input
                className='lk-input'
                value={editName}
                maxLength={maxLength}
                autoFocus
                aria-label='Name'
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => onEditKey(e, item)}
              />
              <button type='button' className='lk-icon' title='Save' onClick={() => saveEdit(item)} disabled={locked || !editName.trim()}>
                <FaCheck />
              </button>
              <button type='button' className='lk-icon' title='Cancel' onClick={() => setEditingId(null)} disabled={busy}>
                <FaTimes />
              </button>
            </li>
          ) : (
            <li
              key={item.id}
              className={`lk-row${onSelect ? ' lk-row--selectable' : ''}${selectedId === item.id ? ' lk-row--active' : ''}${
                dragIndex === index ? ' lk-row--dragging' : ''
              }${dropClass(index)}`}
              draggable={canDrag}
              onDragStart={canDrag ? (e) => onDragStart(e, index) : undefined}
              onDragOver={canDrag ? (e) => onDragOver(e, index) : undefined}
              onDrop={canDrag ? (e) => onDrop(e, index) : undefined}
              onDragEnd={endDrag}
              onClick={() => onSelect?.(item)}
              // Selectable rows work from the keyboard too.
              role={onSelect ? 'button' : undefined}
              tabIndex={onSelect ? 0 : undefined}
              aria-pressed={onSelect ? selectedId === item.id : undefined}
              onKeyDown={(e) => {
                if (onSelect && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  onSelect(item)
                }
              }}
            >
              {onReorder && (
                <span className={`lk-grip${canDrag ? '' : ' lk-grip--off'}`} title='Drag to reorder' aria-hidden='true'>
                  <FaGripVertical />
                </span>
              )}
              <div className='lk-row__text'>
                <span className='lk-row__name'>{item.name}</span>
                {item.meta && <span className='lk-row__meta'>{item.meta}</span>}
              </div>
              {renderControls && (
                // Clicks here mustn't select the row or start a drag.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div className='lk-row__controls' onClick={(e) => e.stopPropagation()}>
                  {renderControls(item)}
                </div>
              )}
              {onReorder && (
                <>
                  <button
                    type='button'
                    className='lk-icon'
                    title='Move up'
                    aria-label={`Move ${item.name} up`}
                    disabled={locked || index === 0}
                    onClick={(e) => {
                      e.stopPropagation()
                      move(index, -1)
                    }}
                  >
                    <FaArrowUp />
                  </button>
                  <button
                    type='button'
                    className='lk-icon'
                    title='Move down'
                    aria-label={`Move ${item.name} down`}
                    disabled={locked || index === shown.length - 1}
                    onClick={(e) => {
                      e.stopPropagation()
                      move(index, 1)
                    }}
                  >
                    <FaArrowDown />
                  </button>
                </>
              )}
              <button
                type='button'
                className='lk-icon'
                title='Rename'
                disabled={locked}
                onClick={(e) => {
                  e.stopPropagation()
                  startEdit(item)
                }}
              >
                <FaPen />
              </button>
              <button
                type='button'
                className='lk-icon lk-icon--danger'
                title='Remove'
                disabled={locked}
                onClick={(e) => {
                  e.stopPropagation()
                  run(() => onDelete(item))
                }}
              >
                <FaTrash />
              </button>
            </li>
          )
        )}
      </ul>

      <div className='lk-add'>
        <input
          className='lk-input'
          value={newName}
          maxLength={maxLength}
          placeholder={addPlaceholder}
          aria-label={addPlaceholder}
          disabled={disabled}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <button type='button' className='lk-btn' onClick={add} disabled={locked || !newName.trim()}>
          <FaPlus /> Add
        </button>
      </div>
    </div>
  )
}

export default EditableNameList
